import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Header } from 'semantic-ui-react';
import { FilterMatchMode } from 'primereact/api';
import { Toast } from 'primereact/toast';
import { classNames } from 'primereact/utils';
import { Messages } from 'primereact/messages';
import { Dialog } from 'primereact/dialog';
import { InputSwitch } from 'primereact/inputswitch';
import { Tag } from 'primereact/tag';
import { Dropdown } from 'primereact/dropdown';

import ApiService from '../services/ApiService';
import { useAuth } from '../context/auth';
import {
	fillMissingCountry,
	getCountryOptions,
	getEnglishCountryName,
	toLocationApiPayload,
} from './countries';

import './Locations.css';

const api = new ApiService();
const countryOptions = getCountryOptions();
const countryOptionsByCode = countryOptions.reduce((options, country) => {
	options[country.code] = country;
	return options;
}, {});

export default function Locations() {
	let emptyLocation = {
		name: '',
		street: '',
		num: '',
		postalCode: '',
		city: '',
		country: '',
		countryCode: '',
		archived: false,
	};
	const [location, setLocation] = useState(emptyLocation);
	const [locations, setLocations] = useState([]);
	const [linkedLocations, setLinkedLocations] = useState([]);
	const [expandedRows, setExpandedRows] = useState(null);
	const [globalFilterValue, setGlobalFilterValue] = useState('');
	const [loading, setLoading] = useState(true);
	const { authTokens } = useAuth();
	const [filters, setFilters] = useState({
		global: { value: null, matchMode: FilterMatchMode.CONTAINS },
	});
	const [submitted, setSubmitted] = useState(false);
	const [locationDialog, setLocationDialog] = useState(false);
	const [inputError, setInputError] = useState(false);
	const [inlineCountryValues, setInlineCountryValues] = useState({});
	const [inputFieldError, setInputFieldError] = useState({
		name: false,
		street: false,
		num: false,
		postalCode: false,
		city: false,
		country: false,
		countryCode: false,
		archived: false,
	});

	const toast = useRef(null);
	const msgs = useRef(null);

	useEffect(() => {
		const headers = {
			'X-Firebase-Auth-Client': authTokens.token,
		};

		const fetchLocations = async () => {
			try {
				const response = await api.getAllLocations(headers);
				setLocations(response.data);
			} catch (error) {
				console.log(error);
			}
		};

		fetchLocations();

		return () => {
			setLocations([]);
		};
	}, [authTokens.token]);

	useEffect(() => {
		const headers = {
			'X-Firebase-Auth-Client': authTokens.token,
		};

		const fetchMachinesToLinkToLocation = async () => {
			try {
				const response = await api.getMachines(headers);

				response.data.forEach((machine) => {
					if (!machine.location) return;

					// find the location of the machine
					const location = locations.find((location) => location.locationId === machine.location.locationId);
					if (location) {
						// add the machine to the location
						if (!location.machines) {
							location.machines = [];
						}

						location.machines.push(machine);
					}
				});

				setLinkedLocations(locations);
				setLoading(false);
			} catch (error) {
				console.log(error);
			}
		};

		fetchMachinesToLinkToLocation();

		return () => {
			setLoading(true);
		};
	}, [authTokens.token, locations]);

	const allowExpansion = (rowData) => {
		return rowData.machines?.length > 0;
	};

	const expandAll = () => {
		let _expandedRows = {};

		linkedLocations.forEach((p) => {
			if (p.machines?.length > 0) {
				_expandedRows[`${p.locationId}`] = true;
			}
		});

		setExpandedRows(_expandedRows);
	};

	const collapseAll = () => {
		setExpandedRows(null);
	};

	const rowExpansionTemplate = (data) => {
		return (
			<div className="subdata">
				<h5 className="text-indigo-500">
					{data.machines.length > 1 ? `Machines (${data.machines.length})` : 'Machine'}
				</h5>
				<ul className="subdata-list">
					{data.machines.map((machine, index) => {
						return (
							<li key={index}>
								{machine.customerName ? (
									<div>
										<span className="font-bold">{machine.customerName} </span>
										<span>({machine.name})</span>
									</div>
								) : (
									<div>
										<span className="font-bold">{machine.name}</span>
									</div>
								)}
							</li>
						);
					})}
				</ul>
			</div>
		);
	};

	const onGlobalFilterChange = (e) => {
		const value = e.target.value;
		let _filters = { ...filters };

		_filters['global'].value = value;

		setFilters(_filters);
		setGlobalFilterValue(value);
	};

	const textEditor = (options) => {
		const rowKey = options.rowData.locationId;
		const value =
			options.field === 'country' && inlineCountryValues[rowKey] !== undefined
				? inlineCountryValues[rowKey]
				: options.value;

		// input error is set to true when the input is empty and the user tries to submit the form add class p-invalid to the empty input
		return (
			<InputText
				type="text"
				value={value}
				onChange={(e) => {
					options.editorCallback(e.target.value);

					if (options.field === 'country') {
						setInlineCountryValues((currentValues) => ({
							...currentValues,
							[rowKey]: e.target.value,
						}));
					}
				}}
				className={`${inputError && inputFieldError[options.field] ? 'p-invalid' : ''} p-inputtext-sm`}
			/>
		);
	};

	const countryOptionTemplate = (option) => {
		if (!option) return null;

		return (
			<div className="country-code-option">
				<span className="country-code-option-code">{option.code}</span>
				<small className="country-code-option-name">{option.name}</small>
			</div>
		);
	};

	const selectedCountryTemplate = (option, props) => {
		return option ? countryOptionTemplate(option) : <span className="p-placeholder">{props.placeholder}</span>;
	};

	const countryCodeDropdown = ({ value, onChange, invalid = false, inputId }) => {
		return (
			<Dropdown
				inputId={inputId}
				value={value || ''}
				options={countryOptions}
				onChange={(e) => onChange(e.value)}
				optionLabel="label"
				optionValue="code"
				itemTemplate={countryOptionTemplate}
				valueTemplate={selectedCountryTemplate}
				filter
				filterBy="code,name"
				filterPlaceholder="Type a code or country"
				resetFilterOnHide
				placeholder="Select a country code"
				className={classNames('country-code-dropdown p-inputtext-sm', { 'p-invalid': invalid })}
				panelClassName="country-code-dropdown-panel"
			/>
		);
	};

	const countryCodeEditor = (options) => {
		return countryCodeDropdown({
			value: options.value,
			onChange: (countryCode) => {
				options.editorCallback(countryCode);

				if (!options.rowData.country || !options.rowData.country.trim()) {
					const country = getEnglishCountryName(countryCode);
					options.rowData.country = country;
					setInlineCountryValues((currentValues) => ({
						...currentValues,
						[options.rowData.locationId]: country,
					}));
				}
			},
			invalid: inputError && inputFieldError.countryCode,
		});
	};

	const booleanEditor = (archived) => {
		// input switch is inverted because the archived value is stored as true when the location is archived
		return (
			<InputSwitch
				checked={!archived.value}
				onChange={(e) => {
					archived.editorCallback(!e.value);
				}}
				className="inputswitch-custom"
			/>
		);
	};

	const onRowEditComplete = async (e) => {
		setInlineCountryValues({});

		if (msgs.current) {
			msgs.current.clear();
		}

		const oldData = e.data;
		const newData = fillMissingCountry(e.newData);
		const location = toLocationApiPayload(newData);

		// if the user didn't change anything, don't update the location
		if (
			oldData.name === newData.name &&
			oldData.city === newData.city &&
			oldData.country === newData.country &&
			oldData.countryCode === newData.countryCode &&
			oldData.street === newData.street &&
			oldData.num === newData.num &&
			oldData.postalCode === newData.postalCode &&
			oldData.archived === newData.archived
		) {
			toast.current.show({
				severity: 'info',
				summary: 'No Changes',
				detail: 'No changes were made',
				life: 3000,
			});

			return;
		}

		const headers = {
			'X-Firebase-Auth-Client': authTokens.token,
		};

		try {
			await api.updateLocation(oldData.locationId, location, headers);
		} catch (error) {
			console.error(error);

			toast.current.show({
				severity: 'error',
				summary: 'Error',
				detail: 'Location Update Failed',
				life: 3000,
			});

			return;
		}

		const updatedLocations = [...linkedLocations];
		const index = updatedLocations.findIndex((location) => location.locationId === oldData.locationId);
		updatedLocations[index] = newData;
		setLinkedLocations(updatedLocations);

		toast.current.show({
			severity: 'success',
			summary: 'Success',
			detail: 'Location Updated',
			life: 3000,
		});

		setInputError(false);
		setInputFieldError({
			name: false,
			street: false,
			num: false,
			postalCode: false,
			city: false,
			country: false,
			countryCode: false,
			archived: false,
		});
	};

	const onRowEditInit = (e) => {
		setInlineCountryValues({});

		if (msgs.current) {
			msgs.current.clear();
			msgs.current.show({
				severity: 'warn',
				summary: 'Editing Location',
				detail:
					'You are now editing a location. This has major consequences on multiple machines linked to this location. Use with caution! ',
				sticky: true,
				closeable: false,
				life: 1000000,
			});
		}
	};

	const onRowEditCancel = (e) => {
		setInlineCountryValues({});

		if (msgs.current) {
			msgs.current.clear();
		}

		toast.current.show({
			severity: 'info',
			summary: 'Cancelled',
			detail: 'Edit Cancelled',
			life: 3000,
		});

		setInputError(false);
		setInputFieldError({
			name: false,
			street: false,
			num: false,
			postalCode: false,
			city: false,
			country: false,
			countryCode: false,
			archived: false,
		});
	};

	const rowEditValidator = (location) => {
		const normalizedLocation = fillMissingCountry(location);
		location.country = normalizedLocation.country;

		const showToast = () => {
			toast.current.show({
				severity: 'error',
				summary: 'Validation Error',
				detail: 'Fields cannot be empty',
				life: 3000,
			});
		};

		switch (true) {
			case !location.name:
				setInputFieldError({ ...inputFieldError, name: true });
				showToast();
				setInputError(true);
				break;
			case !location.street:
				setInputFieldError({ ...inputFieldError, street: true });
				showToast();
				setInputError(true);
				break;
			case !location.num:
				setInputFieldError({ ...inputFieldError, num: true });
				showToast();
				setInputError(true);
				break;
			case !location.postalCode:
				setInputFieldError({ ...inputFieldError, postalCode: true });
				showToast();
				setInputError(true);
				break;
			case !location.city:
				setInputFieldError({ ...inputFieldError, city: true });
				showToast();
				setInputError(true);
				break;
			case !location.countryCode:
				setInputFieldError({ ...inputFieldError, countryCode: true });
				showToast();
				setInputError(true);
				break;
			case !location.country:
				setInputFieldError({ ...inputFieldError, country: true });
				showToast();
				setInputError(true);
				break;
			default:
				setInputError(false);
				break;
		}

		if (
			!location.name ||
			!location.street ||
			!location.num ||
			!location.postalCode ||
			!location.city ||
			!location.country ||
			!location.countryCode
		) {
			return false;
		}

		return true;
	};

	const openNew = () => {
		setLocation(emptyLocation);
		setSubmitted(false);
		setLocationDialog(true);
	};

	const hideDialog = () => {
		setSubmitted(false);
		setLocationDialog(false);
	};

	const saveLocation = async () => {
		setSubmitted(true);

		if (!location) return;
		const normalizedLocation = fillMissingCountry(location);

		if (
			normalizedLocation.name.trim() &&
			normalizedLocation.street.trim() &&
			normalizedLocation.num.trim() &&
			normalizedLocation.postalCode.trim() &&
			normalizedLocation.city.trim() &&
			normalizedLocation.country.trim() &&
			normalizedLocation.countryCode &&
			typeof normalizedLocation.archived === 'boolean'
		) {
			try {
				let _locations = [...locations];
				let _location = { ...normalizedLocation };
				const headers = {
					'X-Firebase-Auth-Client': authTokens.token,
				};

				const locationObjectForApi = toLocationApiPayload(_location);

				await api.addLocation(locationObjectForApi, headers);
				// add the locationId to the location object otherwise react will complain about a missing key
				_location.locationId = locations.length + 1;
				_locations.push(_location);
				setLocations(_locations);

				setLocationDialog(false);

				toast.current.show({
					severity: 'success',
					summary: 'Successful',
					detail: 'Location Created',
					life: 3000,
				});
			} catch (error) {
				console.error(error);

				toast.current.show({
					severity: 'error',
					summary: 'Error',
					detail: 'Location Creation Failed',
					life: 3000,
				});
			}
		} else {
			toast.current.show({
				severity: 'error',
				summary: 'Validation Error',
				detail: 'Fields cannot be empty',
				life: 3000,
			});
		}
	};

	const onInputChange = (e, name) => {
		const val = (e.target && e.target.value) || '';

		let _location = { ...location };
		_location[`${name}`] = val;
		setLocation(_location);
	};

	const onCountryCodeChange = (countryCode) => {
		setLocation((currentLocation) =>
			fillMissingCountry({
				...currentLocation,
				countryCode,
			}),
		);
	};

	const header = (
		<div className="flex justify-content-between">
			<div className="flex justify-content-end">
				<span className="p-input-icon-left">
					<i className="pi pi-search" />
					<InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Keyword Search" />
				</span>
			</div>

			<div className="flex flex-wrap justify-content-end gap-2">
				{!expandedRows ? (
					<Button icon="pi pi-plus" label="Expand All" onClick={expandAll} className="p-button-text" />
				) : (
					<Button icon="pi pi-minus" label="Collapse All" onClick={collapseAll} className="p-button-text" />
				)}

				<Button className="ml-3" label="New" icon="pi pi-plus" onClick={openNew} />
			</div>
		</div>
	);

	const locationDialogFooter = (
		<>
			<Button label="Cancel" icon="pi pi-times" outlined onClick={hideDialog} />
			<Button label="Save" icon="pi pi-check" onClick={saveLocation} />
		</>
	);

	const archivedBodyTemplate = (rowData) => {
		return (
			<Tag
				className="recipes-template-categories"
				value={rowData.archived ? 'Archived' : 'Active'}
				severity={rowData.archived ? 'warning' : 'success'}
			/>
		);
	};

	const countryCodeBodyTemplate = (rowData) => {
		const option = countryOptionsByCode[rowData.countryCode];

		if (!option) return <span className="location-table-field country-code-empty">Not set</span>;

		return <div className="location-table-field">{countryOptionTemplate(option)}</div>;
	};

	return (
		<>
			<Header as="h2">Locations</Header>

			<Toast ref={toast} />
			<Messages ref={msgs} />
			<div className="mt-5 main-datatable">
				<DataTable
					value={linkedLocations}
					expandedRows={expandedRows}
					onRowToggle={(e) => {
						setExpandedRows(e.data);
					}}
					loading={loading}
					rowExpansionTemplate={rowExpansionTemplate}
					dataKey="locationId"
					header={header}
					tableStyle={{ minWidth: '70rem' }}
					paginator
					rows={10}
					globalFilterFields={['name', 'street', 'num', 'postalCode', 'city', 'country', 'countryCode']}
					emptyMessage="No locations found."
					filters={filters}
					alwaysShowPaginator={false}
					removableSort
					onRowEditComplete={onRowEditComplete}
					editMode="row"
					onRowEditInit={onRowEditInit}
					onRowEditCancel={onRowEditCancel}
					rowEditValidator={rowEditValidator}
					size="small"
					sortField="archived"
					sortOrder={1}
				>
					<Column expander={allowExpansion} style={{ width: '2rem' }} />
					<Column
						field="name"
						header="Name"
						sortable
						editor={(options) => textEditor(options)}
						body={(rowData) => <span className="location-table-field">{rowData.name}</span>}
					/>
					<Column
						field="street"
						header="Street"
						sortable
						editor={(options) => textEditor(options)}
						body={(rowData) => <span className="location-table-field">{rowData.street}</span>}
					/>
					<Column
						field="num"
						header="Number"
						editor={(options) => textEditor(options)}
						body={(rowData) => <span className="location-table-field">{rowData.num}</span>}
					/>
					<Column
						field="postalCode"
						header="Zip Code"
						sortable
						editor={(options) => textEditor(options)}
						body={(rowData) => <span className="location-table-field">{rowData.postalCode}</span>}
					/>
					<Column
						field="city"
						header="City"
						sortable
						editor={(options) => textEditor(options)}
						body={(rowData) => <span className="location-table-field">{rowData.city}</span>}
					/>
					<Column
						field="country"
						header="Country"
						sortable
						editor={(options) => textEditor(options)}
						body={(rowData) => <span className="location-table-field">{rowData.country}</span>}
					/>
					<Column
						field="countryCode"
						header="Country code"
						sortable
						editor={(options) => countryCodeEditor(options)}
						body={countryCodeBodyTemplate}
					/>
					<Column
						field="archived"
						header="Status"
						dataType="boolean"
						body={archivedBodyTemplate}
						editor={(options) => booleanEditor(options)}
						sortable
						align={'center'}
					/>
					<Column
						rowEditor
						headerStyle={{ width: '10%', minWidth: '6rem' }}
						bodyStyle={{ textAlign: 'center' }}
					></Column>
				</DataTable>
			</div>

			<Dialog
				visible={locationDialog}
				style={{ width: '32rem' }}
				breakpoints={{ '960px': '75vw', '641px': '90vw' }}
				header="New Location"
				modal
				className="p-fluid"
				footer={locationDialogFooter}
				onHide={hideDialog}
			>
				<div className="field">
					<label htmlFor="name" className="font-bold">
						Name
					</label>
					<InputText
						id="name"
						value={location.name}
						onChange={(e) => onInputChange(e, 'name')}
						required
						autoFocus
						className={'p-inputtext-sm' + classNames({ 'p-invalid': submitted && !location.name })}
					/>
					{submitted && !location.name && <small className="p-error">Name is required.</small>}
				</div>

				<div className="formgrid grid">
					<div className="field col-9">
						<label htmlFor="street" className="font-bold">
							Street
						</label>
						<InputText
							id="street"
							value={location.street}
							onChange={(e) => onInputChange(e, 'street')}
							required
							autoFocus
							className={'p-inputtext-sm' + classNames({ 'p-invalid': submitted && !location.street })}
						/>
						{submitted && !location.street && <small className="p-error">Street is required.</small>}
					</div>

					<div className="field col-3">
						<label htmlFor="street number" className="font-bold">
							Number
						</label>
						<InputText
							id="street number"
							value={location.num}
							onChange={(e) => onInputChange(e, 'num')}
							required
							autoFocus
							className={'p-inputtext-sm' + classNames({ 'p-invalid': submitted && !location.num })}
						/>
						{submitted && !location.num && <small className="p-error">Number is required.</small>}
					</div>
				</div>

				<div className="formgrid grid">
					<div className="field col">
						<label htmlFor="postal code" className="font-bold">
							Postal Code
						</label>
						<InputText
							id="postal code"
							value={location.postalCode}
							onChange={(e) => onInputChange(e, 'postalCode')}
							required
							autoFocus
							className={'p-inputtext-sm' + classNames({ 'p-invalid': submitted && !location.postalCode })}
						/>
						{submitted && !location.postalCode && <small className="p-error">Postal Code is required.</small>}
					</div>

					<div className="field col">
						<label htmlFor="city" className="font-bold">
							City
						</label>
						<InputText
							id="city"
							value={location.city}
							onChange={(e) => onInputChange(e, 'city')}
							required
							autoFocus
							className={'p-inputtext-sm' + classNames({ 'p-invalid': submitted && !location.city })}
						/>
						{submitted && !location.city && <small className="p-error">City is required.</small>}
					</div>

					<div className="field col">
						<label htmlFor="country" className="font-bold">
							Country
						</label>
						<InputText
							id="country"
							value={location.country}
							onChange={(e) => onInputChange(e, 'country')}
							required
							autoFocus
							className={'p-inputtext-sm' + classNames({ 'p-invalid': submitted && !location.country })}
						/>
						{submitted && !location.country && <small className="p-error">Country is required.</small>}
					</div>
				</div>

				<div className="field">
					<label htmlFor="country-code" className="font-bold">
						Country code
					</label>
					{countryCodeDropdown({
						inputId: 'country-code',
						value: location.countryCode,
						onChange: onCountryCodeChange,
						invalid: submitted && !location.countryCode,
					})}
					{submitted && !location.countryCode && (
						<small className="p-error">Country code is required.</small>
					)}
				</div>

				<div className="field col">
					<label htmlFor="archived" className="font-bold block">
						Location Status
					</label>
					<div className="flex align-items-center">
						<InputSwitch
							id="archived"
							checked={!location.archived}
							onChange={(e) => {
								setLocation({ ...location, archived: !e.value });
							}}
						/>

						<p className="ml-2 mt-2" style={{ color: location.archived ? 'red' : 'green' }}>
							{location.archived ? 'Archived' : 'Active'}
						</p>
					</div>
				</div>
			</Dialog>
		</>
	);
}
